import { useRouter } from 'next/router';
import { useContext, type FC, type RefObject } from 'react';
import { GoogleMap } from 'src/components/shared/elements/maps/GoogleMap';
import { NextImage } from 'src/components/shared/elements/NextImage';
import { CLOUD_FRONT_IMAGE_PATH } from 'src/constants/imagePath';
import { useMapProvider } from 'src/hooks/useMapProvider';
import { type ShopSchema } from 'src/models/shop/schema';
import { I18nContext } from 'src/stores/I18n';
import { checkLocaleCn } from 'src/utils/locale';
import { toJSX } from 'src/utils/toJSXHelper';
import { getVJWGuideLink } from 'src/utils/vjw';

type Props = {
  selectedShop?: ShopSchema;
  containerRef: RefObject<HTMLDivElement>;
};

export const DetailContent: FC<Props> = ({ selectedShop, containerRef }) => {
  const { locale } = useRouter();
  const { detail } = useContext(I18nContext).receive;
  const isCounter = selectedShop?.pickupType === 'counter';
  const { openMapApp } = useMapProvider(selectedShop);

  return (
    <div
      className="absolute w-screen right-[-100%] top-0 flex flex-col gap-10 px-5 pb-24 max-h-[80vh] overflow-y-auto hidden-scrollbar max-w-[440px]"
      ref={containerRef}
    >
      <div className="border border-solid border-primary flex p-2.5 justify-around items-center gap-2.5 rounded-[3px]">
        <div className="w-[93px] h-20 shrink-0">
          <NextImage
            src={`${CLOUD_FRONT_IMAGE_PATH}/shared/receive/${isCounter ? 'counter' : 'locker'}.webp`}
            alt={isCounter ? 'counter' : 'locker'}
            width={93}
            height={80}
          />
        </div>
        <div>
          <p className="text-center text-sm font-medium">
            {isCounter ? detail.receiveAtCounter : detail.receiveAtLocker}
          </p>
          <p className="text-sm">{isCounter ? detail.counterDescription : detail.lockerDescription}</p>
          {isCounter ? (
            <a
              href={getVJWGuideLink(locale)}
              className="block text-right text-sm text-gray700 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {detail.vjwRequiredNote}
            </a>
          ) : null}
        </div>
      </div>
      <div>
        <div className="font-medium border-b border-solid border-black p-2.5">{detail.pickupLocationInfo}</div>
        {selectedShop?.pickupTimeList ? (
          <div className="font-medium border-b border-solid border-gray-100 p-2.5 flex gap-5 items-center">
            <p className="w-20">{detail.pickupAvailableTime}</p>
            <div>
              {selectedShop.pickupTimeList.map((list) => (
                <p key={list}>・{list}</p>
              ))}
            </div>
          </div>
        ) : null}
        <div className="font-medium border-b border-solid border-gray-100 p-2.5 flex gap-5 items-center">
          <p className="w-20">{detail.vjwRegistration}</p>
          {isCounter ? <div>{toJSX(detail.required(getVJWGuideLink(locale)))}</div> : <p>{detail.notRequired}</p>}
        </div>
        {selectedShop?.slug ? (
          <div className="font-medium border-b border-solid border-gray-100 p-2.5 flex gap-5 items-center">
            <p className="w-20">{detail.pickupLocationDetail}</p>
            <a
              href={`/kaimono/shops/${selectedShop.slug}`}
              className="underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {detail.view}
            </a>
          </div>
        ) : null}
        <div className="font-medium border-b border-solid border-gray-100 p-2.5 flex gap-5 items-center">
          <p className="w-20">{detail.address}</p>
          <p>{selectedShop?.address}</p>
        </div>
      </div>
      {selectedShop?.latitude && selectedShop?.longitude && !checkLocaleCn(locale) ? (
        <div className="flex flex-col gap-2.5 mb-10">
          <div className="flex justify-between items-center">
            <span className="text-sm">{detail.map}</span>
            <div className="border border-solid border-black text-sm p-1 cursor-pointer" onClick={openMapApp}>
              {detail.viewOnMapApp}
            </div>
          </div>
          <GoogleMap
            latitude={selectedShop.latitude}
            longitude={selectedShop.longitude}
            width="100%"
            height="200"
            locale={locale}
          />
        </div>
      ) : null}
    </div>
  );
};
